// src/components/Steps/FleetAccessResult/FleetAccessResult.jsx
//
// The charging access result (platform cleanup, Phase 3).
//
// Shown after vehicle selection when the backend's access outcome
// (greencharge.access-outcome.v1) is not a plain "continue": the public card
// fallback, a denial, or a charger with no charging flow.
//
// This screen decides nothing. It prints the backend's `title`, `message` and
// `basePrice.label`, and turns the backend's `actions` into buttons. It does
// not read the reason code or any access rule.
//
// One state has wording of its own: the card choice (the backend offers the
// public card flow and the driver has a vehicle on the attempt). There the
// screen is a decision, not a refusal, so it leads with what the driver can
// do and says that paying by card leaves the fleet account alone. That copy
// is the same whatever the backend's reason; every other state prints the
// backend's title and message as they are.
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  FaBolt, FaCarSide, FaCircleInfo, FaShieldHalved, FaTriangleExclamation,
} from 'react-icons/fa6';
import './FleetAccessResult.less';
import { ActionButtonWrapper, EvHeader } from '../../index.js';
import { useSession } from '../../../context/SessionContext.jsx';
import { useToast } from '../../../context/ToastContext.jsx';
import { useCancelAttempt } from '../../../hooks/useCancelAttempt.jsx';
import { STEPS } from '../../../constants/steps.js';
import {
  ACCESS_ACTION,
  accessResultActions,
  isAccessOutcome,
} from '../../../utils/accessOutcome.js';
import { clearedVehicleState } from '../../../utils/clearedVehicleState.js';
import { priceLabelParts } from '../../../utils/priceLabelParts.js';

// Button text per action code. An action is a thing the driver can do, not a
// reason; the backend chooses which ones appear and in what order.
const ACTION_LABELS = Object.freeze({
  [ACCESS_ACTION.CONTINUE_WITH_CARD]: 'Continue with card',
  [ACCESS_ACTION.SELECT_ANOTHER_VEHICLE]: 'Choose another vehicle',
  [ACCESS_ACTION.EXIT]: 'Exit',
});

// Shown only if the backend sent an outcome with no wording at all.
const TITLE_FALLBACK = 'Charging is not available';
const MESSAGE_FALLBACK = 'Charging cannot be started with this vehicle at this charger.';

// The card choice (see the note at the top of this file).
const CARD_CHOICE_TITLE = 'This charger isn’t included in your fleet plan';
const CARD_CHOICE_MESSAGE = 'You can still charge here at the public rate using a card, or choose another vehicle.';
const CARD_CHOICE_ASSURANCE = 'If you pay by card, your fleet account won’t be charged.';

const text = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null);

// The vehicle this result is about, as the driver chose it: a saved
// vehicle's name with its plate, or the plate they typed. Null when the
// attempt has no vehicle.
const vehicleContext = (s) => {
  const plate = text(s.selectedVehicle?.licensePlate) || text(s.licensePlate)
    || text(s.vehicle?.licensePlateRaw);
  const name = text(s.selectedVehicle?.displayName);
  if (!name && !plate) return null;
  return { primary: name || plate, secondary: name && plate && name !== plate ? plate : null };
};

// The charger the driver is standing at: its name and where it is, or its
// public code when the name is not loaded.
const chargerContext = (s) => {
  const name = text(s.stationMeta?.name) || text(s.publicId);
  const location = text(s.stationMeta?.location);
  if (!name && !location) return null;
  return { primary: name || location, secondary: name && location && name !== location ? location : null };
};

// The charger the driver is standing at. cancelSession() resets the whole
// wizard, so these are put back when an attempt is released only to be
// started again without the vehicle.
const stationContext = (s) => ({
  stationId: s.stationId ?? null,
  publicId: s.publicId ?? null,
  connectorId: s.connectorId ?? null,
  stationData: s.stationData ?? null,
  stationStatus: s.stationStatus ?? null,
  stationMeta: s.stationMeta ?? null,
  currency: s.currency || 'BBD',
  accountChoice: s.accountChoice ?? null,
  pendingConnectorRouting: s.pendingConnectorRouting ?? null,
});

const FleetAccessResult = ({ handleIncreaseStep }) => {
  const { state, update, setStep, setAccessOutcome, ensureCustomerSession } = useSession();
  const { showToast } = useToast();
  const { cancelNow, reportResult } = useCancelAttempt();

  const accessOutcome = isAccessOutcome(state.accessOutcome) ? state.accessOutcome : null;
  const headingRef = useRef(null);
  const leavingRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const titleId = useId();
  const priceHeadingId = useId();

  // A full-screen result: move focus to its heading so a screen reader
  // announces it and keyboard focus does not stay on a control that is gone.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // Nothing to show (a reload that kept the step but not the outcome): go
  // back to vehicle selection rather than render an empty screen.
  useEffect(() => {
    if (!accessOutcome && !leavingRef.current) setStep(STEPS.CHARGE_SETUP);
  }, [accessOutcome, setStep]);

  const actionCodes = useMemo(() => accessResultActions(accessOutcome), [accessOutcome]);
  const cardOffered = actionCodes.includes(ACCESS_ACTION.CONTINUE_WITH_CARD);
  // The price the card flow charges. Printed as the backend formatted it,
  // and only next to the option it belongs to.
  const basePriceLabel = cardOffered ? text(accessOutcome?.basePrice?.label) : null;
  // Each amount on its own row when the label can be cut; the label whole
  // when it cannot.
  const priceRows = useMemo(() => (basePriceLabel
    ? priceLabelParts(basePriceLabel) || [{ id: 'price', term: 'Price', value: basePriceLabel }]
    : null), [basePriceLabel]);

  const vehicle = useMemo(() => vehicleContext(state), [state]);
  const charger = useMemo(() => chargerContext(state), [state]);
  // "Your fleet plan" needs a vehicle to be about.
  const cardChoice = cardOffered && Boolean(vehicle);

  // An attempt that already exists and may have been created with a vehicle
  // on it: a saved vehicle that was picked before the session was created, or
  // a vehicle a previous pass through the PIN step attached (the step that
  // routed here records which). The typed-plate journey creates its session
  // WITHOUT a vehicle (the plate is only text until the PIN step attaches
  // it, and this screen comes before that), so that session is already a
  // public one and is kept.
  const sessionMayCarryVehicle = Boolean(state.sessionUid)
    && state.accessOutcomeSessionHasVehicle === true;

  /**
   * Release an attempt that may carry the vehicle, through the one existing
   * cancel path (server-side cancel, then the wizard resets), and put the
   * driver back at this charger with no vehicle. Returns false when the
   * attempt could not be released, in which case nothing else happens.
   */
  const releaseAttemptWithVehicle = useCallback(async () => {
    const keep = stationContext(state);
    const result = await cancelNow();
    if (!result?.success) {
      reportResult(result);
      return false;
    }
    // flushSync: the session context reads its own state through a ref that
    // is updated after render, and the next call (creating the public
    // session) must see this charger and no vehicle.
    flushSync(() => {
      update({
        ...keep,
        ...clearedVehicleState(),
        accessOutcome,
        accessOutcomeSessionHasVehicle: false,
        step: STEPS.FLEET_ACCESS_RESULT,
      });
    });
    return true;
  }, [state, cancelNow, reportResult, update, accessOutcome]);

  /**
   * "Continue with card": the existing public card flow. The vehicle comes
   * off the attempt entirely, so the session is a Public one billed to the
   * driver, and the wizard carries on at the amount screen exactly as it does
   * for a driver who entered no vehicle.
   */
  const continueWithCard = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (sessionMayCarryVehicle) {
        if (!(await releaseAttemptWithVehicle())) return;
      } else {
        // Same reason for flushSync as above: the session created next must
        // not pick the vehicle or its plate up from state.
        flushSync(() => { update(clearedVehicleState()); });
      }

      // The card flow needs a session to pay for. Explicitly without a
      // vehicle; a session that already exists is reused as it is.
      const ensured = await ensureCustomerSession({ vehicleId: null, vehiclePin: null });
      if (!ensured?.ok || !ensured.sessionUid) {
        if (isAccessOutcome(ensured?.accessOutcome)) {
          // The backend has a newer answer for this charger: show that.
          setAccessOutcome(ensured.accessOutcome);
          return;
        }
        showToast({
          message: ensured?.message || "We couldn't start your session. Please try again.",
          type: 'error',
        });
        return;
      }

      leavingRef.current = true;
      setAccessOutcome(null);
      if (typeof handleIncreaseStep === 'function') handleIncreaseStep();
      else setStep(STEPS.AMOUNT_PICKER);
    } finally {
      setBusy(false);
    }
  }, [busy, sessionMayCarryVehicle, releaseAttemptWithVehicle, update, ensureCustomerSession,
    setAccessOutcome, showToast, handleIncreaseStep, setStep]);

  /** "Choose another vehicle": back to vehicle selection, same charger. */
  const selectAnotherVehicle = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (sessionMayCarryVehicle && !(await releaseAttemptWithVehicle())) return;
      leavingRef.current = true;
      update({ ...clearedVehicleState(), accessOutcome: null, accessOutcomeSessionHasVehicle: false });
      setStep(STEPS.CHARGE_SETUP);
    } finally {
      setBusy(false);
    }
  }, [busy, sessionMayCarryVehicle, releaseAttemptWithVehicle, update, setStep]);

  /**
   * "Exit": abandon the attempt through the existing cancel path, which
   * cancels it on the server and resets the wizard. The driver has already
   * been told they cannot charge, so there is no "are you sure?".
   */
  const exit = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    leavingRef.current = true;
    try {
      const result = await cancelNow();
      reportResult(result);
      if (!result?.success) leavingRef.current = false;
    } finally {
      setBusy(false);
    }
  }, [busy, cancelNow, reportResult]);

  const handlers = {
    [ACCESS_ACTION.CONTINUE_WITH_CARD]: continueWithCard,
    [ACCESS_ACTION.SELECT_ANOTHER_VEHICLE]: selectAnotherVehicle,
    [ACCESS_ACTION.EXIT]: exit,
  };

  // Every action carries an explicit onClick. ActionButtonWrapper's own
  // handling of an id such as "cancel" only resets local state and leaves
  // the attempt pending on the server.
  const actions = actionCodes.map((code, index) => ({
    id: `access-${code.toLowerCase().replace(/_/g, '-')}`,
    label: ACTION_LABELS[code],
    type: index === 0 ? 'btn-dark' : 'btn-outline-dark',
    disabled: busy,
    onClick: handlers[code],
  }));

  if (!accessOutcome) return null;

  const Icon = cardOffered ? FaCircleInfo : FaTriangleExclamation;
  const context = [
    vehicle && { id: 'vehicle', term: 'Vehicle', ContextIcon: FaCarSide, ...vehicle },
    charger && { id: 'charger', term: 'Charger', ContextIcon: FaBolt, ...charger },
  ].filter(Boolean);

  return (
    <div className="ev-content-wrapper fleet-access-result-step">
      <EvHeader
        headerAction={{
          label: 'Charging Access',
          action: null,
        }}
      />

      {/* A tab stop, so the keyboard can scroll it when it is taller than the screen. */}
      <div
        className="ev-inner-profile-wrapper fleet-access-result-body"
        role="region"
        aria-labelledby={titleId}
        tabIndex={0}
      >
        <div className={`fleet-access-result-card ${cardOffered ? 'is-card-offered' : 'is-stopped'}`}>
          <div className="fleet-access-result-intro">
            <span className="fleet-access-result-icon" aria-hidden="true">
              <Icon />
            </span>
            <h1 className="fleet-access-result-title" id={titleId} ref={headingRef} tabIndex={-1}>
              {cardChoice ? CARD_CHOICE_TITLE : (text(accessOutcome.title) || TITLE_FALLBACK)}
            </h1>
            <p className="fleet-access-result-message">
              {cardChoice ? CARD_CHOICE_MESSAGE : (text(accessOutcome.message) || MESSAGE_FALLBACK)}
            </p>
          </div>

          {context.length > 0 && (
            <dl className="fleet-access-result-context">
              {context.map(({ id, term, ContextIcon, primary, secondary }) => (
                <div className="fleet-access-result-context-row" key={id}>
                  <span className="fleet-access-result-context-icon" aria-hidden="true">
                    <ContextIcon />
                  </span>
                  <dt>{term}</dt>
                  <dd>
                    <span className="fleet-access-result-context-primary">{primary}</span>
                    {secondary && (
                      <span className="fleet-access-result-context-secondary">{secondary}</span>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          )}

          {priceRows && (
            <section className="fleet-access-result-price" aria-labelledby={priceHeadingId}>
              <div className="fleet-access-result-price-header">
                <h2 className="fleet-access-result-price-title" id={priceHeadingId}>Public rate</h2>
                <span className="fleet-access-result-price-method">Pay by card</span>
              </div>
              <dl className="fleet-access-result-price-rows">
                {priceRows.map((row) => (
                  <div className={`fleet-access-result-price-row is-${row.id}`} key={row.id}>
                    <dt>{row.term}</dt>
                    <dd>{row.value}</dd>
                  </div>
                ))}
              </dl>
              {cardChoice && (
                <p className="fleet-access-result-assurance">
                  <FaShieldHalved aria-hidden="true" />
                  <span>{CARD_CHOICE_ASSURANCE}</span>
                </p>
              )}
            </section>
          )}
        </div>
      </div>

      <ActionButtonWrapper actions={actions} />
    </div>
  );
};

export default FleetAccessResult;
